<template>
	<v-card>
		<v-card-title
			class="d-flex align-center"
		>
			<translate>
				Specifications
			</translate>
			<v-spacer />
			<v-progress-circular
				v-if="loading"
				:size="25"
				indeterminate
				color="primary"
			/>
			<div
				v-else-if="isActive"
				class="text-overline text-primary"
			>
				<translate>Active</translate>
			</div>
			<div
				v-else
				class="text-overline text-disabled"
			>
				<translate>Inactive</translate>
			</div>
		</v-card-title>
		<v-card-text>
			<v-row>
				<v-col>
					<date-text-field
						v-model="productVersion.validfrom"
						:label="`${$gettext('Valid From')} (${$gettext('YYYY-MM-DD')})`"
						variant="outlined"
						:disabled="loading"
						:hide-details="validations.validfrom"
					/>
				</v-col>
			</v-row>
			<v-row>
				<v-col>
					<date-text-field
						v-model="productVersion.validuntil"
						:label="`${$gettext('Valid Until and Including')} (${$gettext('YYYY-MM-DD')})`"
						variant="outlined"
						:disabled="loading"
						:hide-details="validations.validuntil"
					/>
				</v-col>
			</v-row>
			<v-row>
				<v-col>
					<v-select
						v-model="productVersion.invoicerowtypeId"
						:items="invoiceRowTypes"
						item-title="name"
						item-value="id"
						:label="$gettext('Invoice Row Type')"
						variant="outlined"
						:disabled="invoiceLoading"
						:loading="invoiceLoading"
						:hide-details="validations.invoicerowtypeId"
					/>
				</v-col>
			</v-row>
			<v-row>
				<v-col>
					<v-form v-model="validations.startupFee">
						<price-field
							ref="connectionCosts"
							v-model="productVersion.startupFee"
							:label="$gettext('Connection Costs')"
							variant="outlined"
							:disabled="loading"
							:hide-details="validations.startupFee"
						/>
					</v-form>
				</v-col>
			</v-row>
			<v-row>
				<v-col>
					<v-form v-model="validations.frequencycount">
						<tooltip-field
							v-model="productVersion.frequencycount"
							:label="`${$gettext('Duration')} (${$gettext('months')})`"
							:tooltip-text="$gettext('Example: for a duration of 1.5 years, enter 18 here')"
							variant="outlined"
							:rules="[numeric, positive, nonZero]"
							:disabled="loading"
							:hide-details="validations.frequencycount"
						/>
					</v-form>
				</v-col>
			</v-row>
			<v-row>
				<v-col>
					<v-form v-model="validations.periodNotice">
						<tooltip-field
							v-model="productVersion.periodNotice"
							:label="`${$gettext('Notice Period')} (${$gettext('months')})`"
							:tooltip-text="$gettext('Example: fill in a 3 for a 3-month notice period.')"
							variant="outlined"
							:rules="[numeric, positive, nonZero]"
							:disabled="loading"
							:hide-details="validations.periodNotice"
						/>
					</v-form>
				</v-col>
			</v-row>
			<v-row>
				<v-col>
					<v-form v-model="validations.periodMinimum">
						<tooltip-field
							v-model="productVersion.periodMinimum"
							:label="`${$gettext('Minimum Duration')} (${$gettext('months')})`"
							:tooltip-text="$gettext('Enter 12 for example, with a minimum duration of 1 year.')"
							variant="outlined"
							:rules="[numeric, positive, nonZero]"
							:disabled="loading"
							:hide-details="validations.periodMinimum"
						/>
					</v-form>
				</v-col>
			</v-row>
			<v-row>
				<v-col>
					<v-form v-model="validations.paymentPer">
						<tooltip-field
							v-model="productVersion.paymentPer"
							:label="`${$gettext('Payment Period')} (${$gettext('months')})`"
							:tooltip-text="$gettext('The number of periods that a customer prepaid. 3 for a quarter.')"
							variant="outlined"
							:disabled="loading"
							:rules="[numeric, positive]"
							:hide-details="validations.paymentPer"
						/>
					</v-form>
				</v-col>
			</v-row>
			<v-row>
				<v-col>
					<v-form v-model="validations.priceModifier">
						<tooltip-field
							:value="productVersion.priceModifier"
							:label="$gettext('Price Adjustment Factor (decimal number)')"
							:tooltip-text="$gettext('Static adjustment to premium prices for this product version. 1 for 100%, 0.8 for 80% etc.')"
							variant="outlined"
							:disabled="loading"
							:hide-details="validations.priceModifier"
							:rules="[numeric, positive, nonZero]"
							@input="(e) => productVersion.priceModifier = e.replace(',', '.')"
						/>
					</v-form>
				</v-col>
			</v-row>
			<v-row>
				<v-col>
					<v-form v-model="validations.ppmd">
						<v-text-field
							v-model="productVersion.ppmd"
							:label="$gettext('Standard Price Duration (months)')"
							variant="outlined"
							:disabled="loading"
							:rules="[numeric, positive]"
							:hide-details="validations.ppmd"
						/>
					</v-form>
				</v-col>
			</v-row>
			<v-row>
				<v-col>
					<v-form v-model="validations.ppm1D">
						<tooltip-field
							v-model="productVersion.ppm1D"
							:label="`${$gettext('Sale Duration')} #1 (${$gettext('months')})`"
							:tooltip-text="$gettext('Number of months that first term lasts')"
							variant="outlined"
							:disabled="loading"
							:rules="[numeric, positive]"
							:hide-details="validations.ppm1D"
						/>
					</v-form>
				</v-col>
			</v-row>
			<v-row>
				<v-col>
					<v-form v-model="validations.ppm2D">
						<tooltip-field
							v-model="productVersion.ppm2D"
							:label="`${$gettext('Sale Duration')} #2 (${$gettext('months')})`"
							:tooltip-text="$gettext('Number of months that second term lasts')"
							variant="outlined"
							:disabled="loading"
							:rules="[numeric, positive]"
							:hide-details="validations.ppm2D"
						/>
					</v-form>
				</v-col>
			</v-row>
		</v-card-text>
		<v-card-actions>
			<v-spacer />
			<v-btn
				v-can="'productversion.update'"
				variant="text"
				:disabled="loading"
				color="primary"
				@click="postDetails"
			>
				<translate>
					Save
				</translate>
			</v-btn>
		</v-card-actions>
	</v-card>
</template>
<script>

import {
	axios,
} from '../../services/axios-service';
import PriceField from '../atoms/PriceField.vue';
import {
	displayError,
	inform,
} from 'laravel-front-library';
import TooltipField from '../atoms/TooltipField.vue';
import DateTextField from '../atoms/DateTextField.vue';

export default {
	components: {
		DateTextField,
		TooltipField,
		PriceField
	},
	props: {
		productVersionId: {
			required: false,
			type: Number,
			default: 0
		},
		productId: {
			required: true,
			type: String,
		},
		domain: {
			required: true,
			type: String,
		},
	},
	data () {
		return {
			loading: false,
			invoiceLoading: false,
			invoiceRowTypes: [],
			productVersion: {
				validfrom: null,
				validuntil: null,
				invoicerowtypeId: null,
				startupFee: null,
				frequencycount: null,
				periodNotice: null,
				periodMinimum: null,
				paymentPer: null,
				priceModifier: null,
				ppmd: null,
				ppm1D: null,
				ppm2D: null,
			},
			numeric: (v) => !isNaN(v) || 'Must be a number',
			positive: (v) => Number(v) >= 0 || 'Number must be positive',
			nonZero: (v) => Number(v) !== 0 || 'Number can\'t be 0',
			validations: {
				validfrom: true,
				validuntil: true,
				invoicerowtypeId: true,
				startupFee: true,
				frequencycount: true,
				periodNotice: true,
				periodMinimum: true,
				paymentPer: true,
				priceModifier: true,
				ppmd: true,
				ppm1D: true,
				ppm2D: true,
			},
		};
	},
	computed: {
		isActive () {
			const from = new Date(this.productVersion.validfrom);
			const until = new Date(this.productVersion.validuntil);
			const now = new Date();

			return from <= now && now <= until;
		}
	},
	mounted () {
		if (this.productVersionId) {
			this.getDetails();
		}
		this.getInvoiceRowTypes();
	},
	methods: {
		displayError,
		inform,

		/**
		 * retrieve product version details
		 */
		getDetails () {
			this.loading = true;
			axios.get(`/api/v0/productversions?id=${this.productVersionId}`).then(({
				data
			}) => {
				data[0].startupFee = String(data[0].startupFee);
				this.productVersion = data[0];
			}).finally(() => {
				this.loading = false;
			});
		},

		/**
		 * retrieve all available invoice types
		 */
		getInvoiceRowTypes () {
			this.invoiceLoading = true;
			axios.get('/api/v0/invoicerowtypes').then(({
				data
			}) => {
				this.invoiceRowTypes = data;
			}).finally(() => {
				this.invoiceLoading = false;
			});
		},

		/**
		 * update product version details
		 */
		postDetails () {
			this.loading = true;
			const formData = new FormData();

			formData.append('id', this.productId);
			formData.append('productversion', this.productVersionId);
			formData.append('validfrom', this.productVersion.validfrom);
			formData.append('validuntil', this.productVersion.validuntil);
			formData.append('invoicerowtype_id', this.productVersion.invoicerowtypeId);
			formData.append('startup_fee', this.productVersion.startupFee);
			formData.append('frequencycount', this.productVersion.frequencycount);
			formData.append('period_notice', this.productVersion.periodNotice);
			formData.append('period_minimum', this.productVersion.periodMinimum);
			formData.append('payment_per', this.productVersion.paymentPer);
			formData.append('price_modifier', this.productVersion.priceModifier);
			formData.append('ppmd', this.productVersion.ppmd);
			formData.append('ppm1d', this.productVersion.ppm1D);
			formData.append('ppm2d', this.productVersion.ppm2D);

			axios.post(`/bo/products/productversion?id=${this.productId}&productversion_id=${this.productVersionId}`, formData).then(() => {
				this.inform(this.$gettext('Saved Successfully'));
			}).catch((e) => {
				this.displayError(e.response.data);
			}).finally(() => {
				this.loading = false;
				this.getDetails();
			});
		},
	}
};
</script>
